import {
    addDays,
    addMonths,
    differenceInCalendarMonths,
    endOfWeek,
    format,
    getDay,
    getDaysInMonth,
    isAfter,
    isBefore,
    isSameDay,
    isValid,
    parseISO,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './Calendar.module.scss';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
/* Every month gets six week rows, so paging between a 5-row and a 6-row
   month doesn't move the footer (or, in a panel that opened upward, the
   nav arrows under the pointer). */
const CELLS_PER_MONTH = 42;
const MIN_MONTH_WIDTH = 280;
const MONTH_GAP = 24;

const toISO = (date: Date) => format(date, 'yyyy-MM-dd');

const fromISO = (iso?: string | null) => {
    if (!iso) return null;
    const date = parseISO(iso);
    return isValid(date) ? date : null;
};

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');

function Chevron({ direction }: { direction: 'left' | 'right' }) {
    return (
        <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={direction === 'left' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
        </svg>
    );
}

type CalendarProps = {
    /** The selected day, or the first day of a range (YYYY-MM-DD). */
    start?: string;
    /** The last day of a range. Leave unset for a single-date picker. */
    end?: string;
    /** The next pick sets the end of the range, so hovering a later day
     * previews the span from `start` to it. */
    selectingEnd?: boolean;
    min?: string;
    max?: string;
    onSelect: (iso: string) => void;
    /** Show two months side by side once the calendar is wide enough. */
    maxMonths?: 1 | 2;
};

/**
 * Month-grid date picker drawn with the site's own tokens. It stands in
 * for `<input type="date">`, whose popup is browser chrome: a white
 * light-mode calendar that CSS can't restyle.
 */
export function Calendar({ start, end, selectingEnd, min, max, onSelect, maxMonths = 1 }: CalendarProps) {
    const startDate = fromISO(start);
    const endDate = fromISO(end);
    const minDate = fromISO(min);
    const maxDate = fromISO(max);

    const clamp = (date: Date) => {
        if (minDate && isBefore(date, minDate)) return minDate;
        if (maxDate && isAfter(date, maxDate)) return maxDate;
        return date;
    };

    const rootRef = useRef<HTMLDivElement | null>(null);
    const monthsRef = useRef<HTMLDivElement | null>(null);
    const focusPendingRef = useRef(false);

    const [monthCount, setMonthCount] = useState(1);
    const [view, setView] = useState(() => startOfMonth(startDate ?? clamp(new Date())));
    const [focusDate, setFocusDate] = useState(() => startDate ?? clamp(new Date()));
    const [hoverDate, setHoverDate] = useState<Date | null>(null);

    useLayoutEffect(() => {
        const el = rootRef.current;
        if (maxMonths < 2 || !el || typeof ResizeObserver === 'undefined') return;
        const measure = () => setMonthCount(el.clientWidth >= MIN_MONTH_WIDTH * 2 + MONTH_GAP ? 2 : 1);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [maxMonths]);

    // Keyboard moves land on a day that may only exist after the re-render
    // (a new month), so the focus call waits for the commit.
    useEffect(() => {
        if (!focusPendingRef.current) return;
        focusPendingRef.current = false;
        monthsRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${toISO(focusDate)}"]`)?.focus();
    });

    const lastMonth = addMonths(view, monthCount - 1);
    const canGoBack = !minDate || isAfter(view, startOfMonth(minDate));
    const canGoForward = !maxDate || isBefore(lastMonth, startOfMonth(maxDate));
    const isDisabled = (date: Date) =>
        Boolean((minDate && isBefore(date, minDate)) || (maxDate && isAfter(date, maxDate)));
    const isVisible = (date: Date) => {
        const offset = differenceInCalendarMonths(date, view);
        return offset >= 0 && offset < monthCount;
    };

    // Exactly one day sits in the tab order (roving tabindex); arrow keys
    // move between the rest.
    const tabbableDate = isVisible(focusDate) && !isDisabled(focusDate) ? focusDate : clamp(view);

    const page = (delta: number) => {
        setView((current) => addMonths(current, delta));
        setFocusDate((current) => clamp(addMonths(current, delta)));
    };

    const moveFocus = (date: Date) => {
        const next = clamp(date);
        const offset = differenceInCalendarMonths(next, view);
        if (offset < 0) setView(startOfMonth(next));
        else if (offset >= monthCount) setView(addMonths(startOfMonth(next), -(monthCount - 1)));
        setFocusDate(next);
        focusPendingRef.current = true;
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const current = fromISO((event.target as HTMLElement).dataset?.iso);
        if (!current) return;
        const moves: Record<string, () => Date> = {
            ArrowLeft: () => addDays(current, -1),
            ArrowRight: () => addDays(current, 1),
            ArrowUp: () => addDays(current, -7),
            ArrowDown: () => addDays(current, 7),
            Home: () => startOfWeek(current),
            End: () => endOfWeek(current),
            PageUp: () => addMonths(current, -1),
            PageDown: () => addMonths(current, 1),
        };
        const move = moves[event.key];
        if (!move) return;
        event.preventDefault();
        moveFocus(move());
    };

    // While the end is still being picked, the span follows the pointer.
    const previewEnd = selectingEnd && startDate && hoverDate && isAfter(hoverDate, startDate) ? hoverDate : null;
    const spanEnd = previewEnd ?? endDate;
    const today = new Date();

    const renderMonth = (month: Date) => {
        const leading = getDay(month);
        const daysInMonth = getDaysInMonth(month);
        const cells: React.ReactNode[] = [];

        for (let i = 0; i < leading; i += 1) {
            cells.push(<div key={`lead-${i}`} className={styles.cell} aria-hidden="true"><span className={styles.spacer} /></div>);
        }

        for (let dayOfMonth = 1; dayOfMonth <= daysInMonth; dayOfMonth += 1) {
            const date = new Date(month.getFullYear(), month.getMonth(), dayOfMonth);
            const iso = toISO(date);
            const column = getDay(date);
            const disabled = isDisabled(date);
            const isStart = Boolean(startDate && isSameDay(date, startDate));
            const isSpanEnd = Boolean(spanEnd && isSameDay(date, spanEnd));
            const isPreviewEnd = Boolean(previewEnd && isSameDay(date, previewEnd));
            // A previewed span replaces the committed one, end circle included.
            const selected = isStart || Boolean(!previewEnd && endDate && isSameDay(date, endDate));
            const inSpan = Boolean(
                startDate && spanEnd && isAfter(spanEnd, startDate)
                && !isBefore(date, startDate) && !isAfter(date, spanEnd)
            );

            // The band runs cell to cell behind the day circles, starting
            // and stopping at the circles' centres and rounding off where
            // a week row (or the month) breaks it.
            const rowStart = column === 0 || dayOfMonth === 1;
            const rowEnd = column === 6 || dayOfMonth === daysInMonth;
            const showBand = inSpan && !(isStart && rowEnd) && !(isSpanEnd && rowStart);

            cells.push(
                <div key={iso} className={styles.cell}>
                    {showBand && (
                        <span
                            aria-hidden="true"
                            className={cx(
                                styles.band,
                                previewEnd && styles.bandPreview,
                                isStart && styles.bandFromCentre,
                                isSpanEnd && styles.bandToCentre,
                                !isStart && rowStart && styles.bandRoundStart,
                                !isSpanEnd && rowEnd && styles.bandRoundEnd,
                            )}
                        />
                    )}
                    <button
                        type="button"
                        data-iso={iso}
                        className={cx(
                            styles.day,
                            selected && styles.daySelected,
                            isPreviewEnd && styles.dayPreviewEnd,
                            isSameDay(date, today) && styles.dayToday,
                        )}
                        disabled={disabled}
                        tabIndex={isSameDay(date, tabbableDate) ? 0 : -1}
                        aria-pressed={selected}
                        aria-current={isSameDay(date, today) ? 'date' : undefined}
                        aria-label={format(date, 'EEEE, MMMM d, yyyy')}
                        onClick={() => {
                            setFocusDate(date);
                            onSelect(iso);
                        }}
                        onMouseEnter={() => !disabled && setHoverDate(date)}
                        onFocus={() => setHoverDate(date)}
                    >
                        {dayOfMonth}
                    </button>
                </div>
            );
        }

        while (cells.length < CELLS_PER_MONTH) {
            cells.push(<div key={`trail-${cells.length}`} className={styles.cell} aria-hidden="true"><span className={styles.spacer} /></div>);
        }

        return cells;
    };

    const months = Array.from({ length: monthCount }, (_, i) => addMonths(view, i));

    return (
        <div ref={rootRef} className={styles.root}>
            <div
                ref={monthsRef}
                className={styles.months}
                style={{ gridTemplateColumns: `repeat(${monthCount}, minmax(0, 1fr))`, columnGap: MONTH_GAP }}
                onKeyDown={onKeyDown}
                onMouseLeave={() => setHoverDate(null)}
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHoverDate(null);
                }}
            >
                {months.map((month, index) => (
                    <div key={toISO(month)} className={styles.month}>
                        <div className={styles.monthHeader}>
                            {index === 0 ? (
                                <button type="button" className={styles.navButton} onClick={() => page(-1)} disabled={!canGoBack} aria-label="Previous month">
                                    <Chevron direction="left" />
                                </button>
                            ) : <span />}
                            <span className={styles.monthLabel} aria-live="polite">{format(month, 'MMMM yyyy')}</span>
                            {index === months.length - 1 ? (
                                <button type="button" className={styles.navButton} onClick={() => page(1)} disabled={!canGoForward} aria-label="Next month">
                                    <Chevron direction="right" />
                                </button>
                            ) : <span />}
                        </div>
                        <div className={styles.weekdays} aria-hidden="true">
                            {WEEKDAYS.map((weekday) => <span key={weekday}>{weekday}</span>)}
                        </div>
                        <div className={styles.days}>{renderMonth(month)}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
